import React, { useEffect, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import useUserProfile from '../../../../customHooks/useUserProfile';
import { useZinantisMembers } from '../../../contextsProperty/ContextZinantisMembers';
import LoadingMembers from './LoadingMembers';
import MembersFiltersBar from './filters/MembersFiltersBar';
import '../../../../stylesheets/stylesLayout/zinantisMembers.scss';
import IconButton from '../../../../muiComponents/IconButton';
import Tooltip from '../../../../muiComponents/Tooltip';
import MembersTableView from './tableView/MembersTableView';
import MembersCardsView from './cardsView/MembersCardsView';
import useIsMobile from '../../../../customHooks/useIsMobileQuery';
import style from '../../../../stylesheets/variables.module.scss';

const Members = () => {
    const { translate } = useUserProfile();
    const {
        accountManagmentSegments,
        members,
        filters,
        updateFilters,
        isLoading,
    } = useZinantisMembers();

    const [isTableView, setisTableView] = useState(true);

    const filteredMembers = useMemo(() => {
        let filtered = members;

        if (filters.idCountry) {
            filtered = filtered.filter(
                member => member.idCountry === filters.idCountry
            );
        }
        if (filters.name) {
            filtered = filtered.filter(member =>
                member?.name
                    ?.toLocaleLowerCase()
                    .includes(filters.name.toLowerCase())
            );
        }
        if (filters.connected !== 'all') {
            filtered = filtered.filter(member => {
                const filterValue = filters.connected === 'connected';
                return member.connected === filterValue;
            });
        }
        return filtered;
    }, [filters, members]);

    const membersWithoutSegment = useMemo(() => {
        const validSegmentIds = accountManagmentSegments.map(s =>
            parseInt(s.id)
        );

        return filteredMembers.filter(
            member =>
                !validSegmentIds.includes(
                    Number(member.idAccountManagmentSegment)
                ) &&
                member?.name
                    ?.toLocaleLowerCase()
                    .includes(filters.name.toLowerCase())
        );
    }, [filteredMembers, accountManagmentSegments, filters.name]);

    const connectedMembers = useMemo(() => {
        return filteredMembers.filter(member => member.connected);
    }, [filteredMembers]);

    const newMembers = useMemo(() => {
        return filteredMembers.filter(member => member.isNew);
    }, [filteredMembers]);

    useEffect(() => {
        return () => updateFilters('name', '');
    }, []);

    // list overflows on mobile so there will be no table view
    const mobileBreakpoint = parseInt(style.md);
    const isMobile = useIsMobile(mobileBreakpoint);
    useEffect(() => {
        if (isMobile && isTableView) {
            setisTableView(false);
        }
    }, [isMobile, isTableView]);

    return (
        <>
            {isLoading ? (
                <LoadingMembers />
            ) : (
                <div className='zinantis-members__page'>
                    <div className='flex flex-column gap-10'>
                        <div className='flex flex-between flex-align-center'>
                            <Typography variant='h1'>
                                {translate('members')}
                            </Typography>
                            {isMobile ? (
                                ''
                            ) : (
                                <Tooltip
                                    titleText={
                                        isTableView
                                            ? 'switch_grid_view'
                                            : 'switch_table_view'
                                    }
                                >
                                    <IconButton
                                        onClick={() =>
                                            setisTableView(!isTableView)
                                        }
                                        iconName={
                                            isTableView
                                                ? 'GridView'
                                                : 'TableChart'
                                        }
                                    />
                                </Tooltip>
                            )}
                        </div>
                        <MembersFiltersBar />
                    </div>
                    {isTableView ? (
                        <MembersTableView
                            members={filteredMembers}
                            segments={accountManagmentSegments}
                            membersWithoutSegment={membersWithoutSegment}
                            connectedMembers={connectedMembers}
                            newMembers={newMembers}
                        />
                    ) : (
                        <MembersCardsView
                            members={filteredMembers}
                            segments={accountManagmentSegments}
                            membersWithoutSegment={membersWithoutSegment}
                            connectedMembers={connectedMembers}
                            newMembers={newMembers}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default Members;
